import { HEADER_BYTES, MALFORMED, OK, assertField, checkHeader, putHeader, putU16, putU32, readU16, readU32, type DecodeVerdict } from "./binary.ts";
import { MessageType } from "./catalogue.ts";
import { MAX_U16, MAX_U32 } from "./limits.ts";

/**
 * The `combat` frame (ADR-074 decision 3, PROTOCOL.md 5.7): server to client, one frame a tick of the events meant
 * for one page, after its snapshot and its `vitals`. The header's tick names the combat phase that produced them;
 * each event is 10 bytes (kind, flags, source, target, ability, amount), so a page skips a kind it does not know. A
 * map server writes the frame straight into a buffer its session allocates at join (`putCombatEvent`,
 * `putCombatHeader`), so a send allocates nothing; the decoder never throws (ADR-030).
 */

/**
 * Bytes before the first event: the frame header, the tick and the count.
 */
export const COMBAT_HEADER_BYTES = HEADER_BYTES + 4 + 1;

/**
 * Bytes of one event.
 */
export const COMBAT_EVENT_BYTES = 10;

/**
 * The most events a map server puts in one page's frame a tick, its own first (ADR-074 decision 4). The layout
 * carries up to 255.
 */
export const MAX_COMBAT_EVENTS = 16;

const MAX_COUNT = 255;
const MAX_BYTE = 255;
const DAMAGE_TYPE_SHIFT = 2;
const DAMAGE_TYPE_MASK = 0b11;

/**
 * The event kinds. `used` and `refused` answer the page's own act; `hit` and `death` reach the pages of the source
 * and the target; `heal` (ADR-091 decision 11) reaches them as a `hit` does, its amount the health restored;
 * `cooldown` tells the page's own character, in the first frame after its join, an ability whose cooldown is
 * running, its amount the ticks until ready from the frame's tick as `used` counts them (ADR-084's note for P2.3-T8).
 */
export const CombatEventKind = { Used: 1, Hit: 2, Refused: 3, Death: 4, Heal: 5, Cooldown: 6 } as const;
export type CombatEventKind = (typeof CombatEventKind)[keyof typeof CombatEventKind];

/**
 * Why an act was refused, in ADR-073 decision 5's order of validation, and `busy` for a full act queue; a `refused`
 * event carries it as its amount. `passage` and `targetPassage` stand where `peace` would for a targeted act between two
 * characters when the actor's or the target's safe passage counts (ADR-076's amendment of 2026-10-03). `held` answers
 * the act of a character a Game Master has frozen, checked before every other reason, and `targetHeld` a targeted act
 * aimed at one (ADR-056's amendment of 2026-10-06, decision 45). `helpFlagged`, `helpParty` and `helpSheltered` refuse a
 * help on an ally (ADR-076's amendment of 2026-10-08, D4): an unflagged helper and a Flagged target, a target the helper's
 * party does not hold (or any stranger's help on a character in a fight with creatures), and a helper standing where
 * creatures cannot enter naming a character in such a fight.
 */
export const RefusalReason = { Dead: 1, Stunned: 2, Bar: 3, Cooldown: 4, Cost: 5, Target: 6, View: 7, Range: 8, Sight: 9, Peace: 10, Busy: 11, Passage: 12, TargetPassage: 13, Held: 14, TargetHeld: 15, HelpFlagged: 16, HelpParty: 17, HelpSheltered: 18 } as const;
export type RefusalReason = (typeof RefusalReason)[keyof typeof RefusalReason];

/**
 * The bits of a `hit` event's flags around the damage type (bits 2 and 3) and the negation cause (bits 4 to 6):
 * critical, killing blow, and `Effect` (bit 7, ADR-079's amendment of 2026-10-02), set on a `hit` or a `death` a
 * periodic effect's tick caused, whose `ability` field then carries the effect's number rather than an ability's.
 */
export const HitFlag = { Critical: 1, KillingBlow: 2, Effect: 0x80 } as const;

/**
 * Why a `hit` dealt nothing, in its flags' bits 4 to 6 (ADR-079 decision 6): shields absorbed what mitigation left
 * (`Blocked`), mitigation from an effect took it all (`Immune`), or a dodge (`Dodge`, declared for Agility's dodge chance
 * and Evasion; nothing sets it yet). 4 to 7 wait for a parry. A page before this ignores the bits.
 */
export const NegationCause = { None: 0, Blocked: 1, Immune: 2, Dodge: 3 } as const;
export type NegationCause = (typeof NegationCause)[keyof typeof NegationCause];

const NEGATION_SHIFT = 4;
const NEGATION_MASK = 0b111;

/**
 * One event, decoded or to be encoded.
 */
export interface CombatEvent {
  kind: number;
  flags: number;
  source: number;
  target: number;
  ability: number;
  amount: number;
}

/**
 * A decoded `combat` frame, reused across frames: its tick, how many events it held and the events, the first
 * `count` of `events` valid.
 */
export interface CombatRecord {
  tick: number;
  count: number;
  readonly events: CombatEvent[];
}

/**
 * A fresh record for `decodeCombat` to fill.
 *
 * @returns An empty record.
 */
export function newCombatRecord(): CombatRecord {
  return { tick: 0, count: 0, events: [] };
}

/**
 * Bytes of a frame of a number of events.
 *
 * @param count The events.
 * @returns `8 + 10 * count`.
 */
export function combatBytes(count: number): number {
  return COMBAT_HEADER_BYTES + COMBAT_EVENT_BYTES * count;
}

/**
 * A `hit` event's flags.
 *
 * @param critical Whether the hit was critical.
 * @param killingBlow Whether it took its target from above 0 to 0.
 * @param damageType The damage type's place in the rules' damage types: 0 physical, 1 thermal, 2 kinetic, 3 poison.
 * @param cause Why the hit dealt nothing (`NegationCause`), `None` for a hit that dealt damage.
 * @param effect Whether a periodic effect's tick dealt it, so the event's `ability` is the effect's number.
 * @returns The flags.
 * @throws Error when the damage type does not fit two bits or the cause three.
 */
export function hitFlags(critical: boolean, killingBlow: boolean, damageType: number, cause = 0, effect = false): number {
  assertField(damageType, DAMAGE_TYPE_MASK, `damage type ${damageType}`);
  assertField(cause, NEGATION_MASK, `negation cause ${cause}`);
  return (critical ? HitFlag.Critical : 0) | (killingBlow ? HitFlag.KillingBlow : 0) | (damageType << DAMAGE_TYPE_SHIFT) | (cause << NEGATION_SHIFT) | (effect ? HitFlag.Effect : 0);
}

/**
 * Whether a `hit` or `death` event came from a periodic effect's tick, so its `ability` field is an effect's number.
 *
 * @param flags The event's flags.
 * @returns True when bit 7 is set.
 */
export function isEffectEvent(flags: number): boolean {
  return (flags & HitFlag.Effect) !== 0;
}

/**
 * Why a `hit` event dealt nothing, from its flags.
 *
 * @param flags The flags.
 * @returns 0 to 7: `NegationCause.None` for a hit that dealt damage.
 */
export function negationOfFlags(flags: number): number {
  return (flags >>> NEGATION_SHIFT) & NEGATION_MASK;
}

const USED_STEP_MASK = 0b11;

/**
 * The bits of a `used` event's flags above the strip step (bits 0 and 1): `Unpaid` (bit 2, ADR-117 decision 9), set on
 * the `used` of a basic blow swung unpaid, one whose cost its caster could not pay, which took nothing and deals the
 * unpaid share of a paid hit. A page before it ignores the bit.
 */
export const UsedFlag = { Unpaid: 4 } as const;

/**
 * A `used` event's flags (PROTOCOL.md 5.7, 6.11.0 and 6.32.0): the strip step the map server chose for the act in bits
 * 0 and 1, 0 for the ability's own strip (ADR-073's amendment "the Strike combo and the Heavy Strike chain", item 3),
 * and in bit 2 whether a basic blow swung unpaid (ADR-117 decision 9).
 *
 * @param step The step, 0 to 3.
 * @param unpaid Whether the act is a basic blow swung unpaid; only `true` sets the bit.
 * @returns The flags.
 * @throws Error when the step does not fit two bits.
 */
export function usedFlags(step: number, unpaid = false): number {
  assertField(step, USED_STEP_MASK, `strip step ${step}`);
  return unpaid === true ? step | UsedFlag.Unpaid : step;
}

/**
 * The strip step a `used` event's flags carry; a page ignores the other bits.
 *
 * @param flags The flags.
 * @returns 0 to 3.
 */
export function stepOfUsedFlags(flags: number): number {
  return flags & USED_STEP_MASK;
}

/**
 * Whether a `used` event's flags say its basic blow swung unpaid (ADR-117 decision 9); every other bit is ignored.
 *
 * @param flags The flags.
 * @returns True when bit 2 is set.
 */
export function unpaidOfUsedFlags(flags: number): boolean {
  return (flags & UsedFlag.Unpaid) !== 0;
}

/**
 * The damage type a `hit` event's flags carry.
 *
 * @param flags The flags.
 * @returns 0 to 3.
 */
export function damageTypeOfFlags(flags: number): number {
  return (flags >>> DAMAGE_TYPE_SHIFT) & DAMAGE_TYPE_MASK;
}

/**
 * Writes one event at its place in a frame through plain byte stores. The caller has sized the buffer and checked the
 * fields' widths, as a map server does with values it computed.
 *
 * @param buffer The frame.
 * @param index The event's place, from 0.
 * @param kind The kind.
 * @param flags The flags.
 * @param source The source handle, or `NO_HANDLE`.
 * @param target The target handle, or `NO_HANDLE`.
 * @param ability The ability's number, or 0.
 * @param amount The amount.
 */
export function putCombatEvent(buffer: Uint8Array, index: number, kind: number, flags: number, source: number, target: number, ability: number, amount: number): void {
  const at = COMBAT_HEADER_BYTES + COMBAT_EVENT_BYTES * index;
  buffer[at] = kind;
  buffer[at + 1] = flags;
  putU16(buffer, at + 2, source);
  putU16(buffer, at + 4, target);
  putU16(buffer, at + 6, ability);
  putU16(buffer, at + 8, amount);
}

/**
 * Writes a frame's header once its events are in place.
 *
 * @param buffer The frame.
 * @param tick The tick whose combat phase produced the events.
 * @param count The events written, 1 to 255.
 * @returns The frame's length in bytes.
 */
export function putCombatHeader(buffer: Uint8Array, tick: number, count: number): number {
  putHeader(buffer, MessageType.Combat);
  putU32(buffer, HEADER_BYTES, tick);
  buffer[HEADER_BYTES + 4] = count;
  return combatBytes(count);
}

/**
 * Encodes a `combat` frame into a caller-owned buffer, checking every field.
 *
 * @param buffer Destination, at least `combatBytes(events.length)` long.
 * @param tick The tick.
 * @param events 1 to 255 events.
 * @returns Bytes written.
 * @throws Error when the count is 0 or above 255, the buffer is too small, or a field does not fit its width.
 */
export function encodeCombat(buffer: Uint8Array, tick: number, events: readonly Readonly<CombatEvent>[]): number {
  if (events.length < 1 || events.length > MAX_COUNT) {
    throw new Error(`combat event count ${events.length} is not 1 to ${MAX_COUNT}`);
  }
  const length = combatBytes(events.length);
  if (buffer.length < length) {
    throw new Error(`buffer of ${buffer.length} bytes cannot hold a ${length}-byte combat frame`);
  }
  assertField(tick, MAX_U32, `combat tick ${tick}`);
  for (let index = 0; index < events.length; index += 1) {
    const event = events[index] as Readonly<CombatEvent>;
    assertField(event.kind, MAX_BYTE, `combat event kind ${event.kind}`);
    assertField(event.flags, MAX_BYTE, `combat event flags ${event.flags}`);
    assertField(event.source, MAX_U16, `combat event source ${event.source}`);
    assertField(event.target, MAX_U16, `combat event target ${event.target}`);
    assertField(event.ability, MAX_U16, `combat event ability ${event.ability}`);
    assertField(event.amount, MAX_U16, `combat event amount ${event.amount}`);
    putCombatEvent(buffer, index, event.kind, event.flags, event.source, event.target, event.ability, event.amount);
  }
  return putCombatHeader(buffer, tick, events.length);
}

/**
 * Decodes a `combat` frame into a record. Never throws: a frame shorter than its header, a count of 0, a length other
 * than `8 + 10 * count` or another type is malformed, and another version a mismatch. Kinds and flags are read as
 * they are, for the page to skip what it does not know.
 *
 * @param frame The whole frame.
 * @param out Record to fill; untouched unless the verdict is `ok`.
 * @returns The verdict.
 */
export function decodeCombat(frame: Uint8Array, out: CombatRecord): DecodeVerdict {
  if (frame.length < COMBAT_HEADER_BYTES) {
    return MALFORMED;
  }
  const count = frame[HEADER_BYTES + 4] as number;
  if (count === 0) {
    return MALFORMED;
  }
  const header = checkHeader(frame, MessageType.Combat, combatBytes(count));
  if (header.kind !== "ok") {
    return header;
  }
  const events = out.events;
  for (let index = 0; index < count; index += 1) {
    const at = COMBAT_HEADER_BYTES + COMBAT_EVENT_BYTES * index;
    let event = events[index];
    if (event === undefined) {
      event = { kind: 0, flags: 0, source: 0, target: 0, ability: 0, amount: 0 };
      events.push(event);
    }
    event.kind = frame[at] as number;
    event.flags = frame[at + 1] as number;
    event.source = readU16(frame, at + 2);
    event.target = readU16(frame, at + 4);
    event.ability = readU16(frame, at + 6);
    event.amount = readU16(frame, at + 8);
  }
  out.tick = readU32(frame, HEADER_BYTES);
  out.count = count;
  return OK;
}
